import React, { useEffect, useRef, useState } from 'react';

import { Alert, Button, Stack, Typography } from '@mui/material';

import {
  personalViewFailure,
  useCreatePersonalView,
  useGetPersonalViews,
} from '~/hooks/apis/reporting/usePersonalViews';
import useCurrentUser from '~/hooks/useCurrentUser';

import { LEGACY_FINANCIAL_VIEWS_KEY, compatibleLegacyViews, importLegacyViews } from './legacyViews';

/** Browser records are never removed or changed. Import requires a deliberate account-private copy. */
export default function LegacyViewsImport({ lensId }: { lensId: string }) {
  const [legacy] = useState(() => {
    try {
      return compatibleLegacyViews(localStorage.getItem(LEGACY_FINANCIAL_VIEWS_KEY));
    } catch {
      return { views: [], incompatible: 0 };
    }
  });
  const saved = useGetPersonalViews(lensId);
  const create = useCreatePersonalView(lensId);
  const { isAuthenticated, user } = useCurrentUser();
  const identity = useRef<string | null>(null);
  identity.current = isAuthenticated && user?.email ? `${user.email.trim().toLowerCase()}:${lensId}` : null;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const [message, setMessage] = useState('');
  const remaining = legacy.views.filter(
    (view) =>
      !saved.data?.views.some(
        (existing) =>
          !existing.deletedAt &&
          existing.name.toLowerCase() === view.name.toLowerCase() &&
          existing.state.search === view.state.search,
      ),
  );
  if (!remaining.length && !legacy.incompatible && !message) return null;
  const importViews = async () => {
    const initiatingIdentity = identity.current;
    const result = await importLegacyViews(
      remaining,
      () => Boolean(initiatingIdentity && mounted.current && identity.current === initiatingIdentity),
      (view) => create.mutateAsync(view),
    );
    if (!result) return;
    setMessage(
      result.error
        ? `${result.imported} imported. ${personalViewFailure(result.error, 'Some browser views could not be imported.')} Browser copies are unchanged.`
        : `${result.imported} views imported into your account. Browser copies are unchanged.`,
    );
  };
  return (
    <Stack spacing={0.5} sx={{ mt: 1, mb: 1 }}>
      {remaining.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary">
            This browser has saved views. Import only views that belong to you.
          </Typography>
          <Button
            size="small"
            disabled={!saved.isSuccess || create.isPending}
            sx={{ alignSelf: 'flex-start' }}
            onClick={() => void importViews()}
          >
            {create.isPending ? 'Importing…' : `Import ${remaining.length} browser views`}
          </Button>
        </>
      )}
      {legacy.incompatible > 0 && (
        <Alert severity="info">
          {legacy.incompatible} older browser views need to be recreated with this report’s current filters. The
          originals are unchanged.
        </Alert>
      )}
      {message && <Alert severity={create.isError ? 'warning' : 'success'}>{message}</Alert>}
    </Stack>
  );
}
